/**
 * Params that are used in routing folder structure
 * For example /[tenant]/[subsidiary] directory inside app folder.
 * */
export const ROUTE_PARAMS = {
  /** Tenant display_name */
  TENANT: 'tenant',
  /** Subsidiary id */
  SUBSIDIARY: 'subsidiary',
  /** Subsidiary id when we manage subsidiaries */
  EDIT_SUBSIDIARY: 'editSubsidiary',
  /** Used for any other entity id */
  ENTITY_ID: 'id',
  /** Used for any additional details id */
  DETAILS_ID: 'detailsId',
  /** Used for attachment downloading */
  FILE_NAME: 'fileName',
  // TODO: Remove this and use ENTITY_ID instead
  ACCOUNT: 'account',
  GROUP: 'group',
  REPORT: 'report',
  /** Used for journal entry type */
  JE_ENTRY_TYPE: 'jeType',
  // TODO: Remove this and use ENTITY_ID instead
  JE_ENTRY_ID: 'jeEntryId',
  PERIOD: 'period',
  SLUG: 'slug',
  SOURCE_ID: 'sourceId',
  VERSION_ID: 'versionId',
} as const;

type RouteParamKeys = (typeof ROUTE_PARAMS)[keyof typeof ROUTE_PARAMS];

/**
 * Common search queries that are used across app.
 * */
export const ROUTE_QUERIES = {
  /** Used to navigate back to previous page */
  ROUTE_BACK: 'back',
  /** Used to indicate pending changes with forms */
  PENDING_CHANGES: 'pending-changes',
  /** Deep-link into matched bank-rec with Posting Status pre-selected */
  ADJUSTMENT_POSTING_STATUS: 'posting_status',
  /** The matching set the auto-ict match pages are scoped to */
  MATCHING_SET_ID: 'matchingSetId',
  /**
   * Opens an entity page read-only (`=1`). Used by the matching-group page,
   * which a matching set's groups are only ever *viewed* through — editing one
   * side of a set behind the set's back is not a thing (NAS-2217).
   */
  VIEW_ONLY: 'viewOnly',
  /** Deep-link cap for matched bank-rec date filter (YYYY-MM-DD) */
  MATCH_TO_DATE: 'to_date',
  COMMENT_ID: 'commentId',
  COMMENT_HASH: '#cm_',
  /**
   * The room's own entity id, passed alongside `id` for nested/line-level
   * comment rooms (metadataIdSource='thread') where `id` is the line key. Names
   * the page to open — e.g. the flux report a commented line belongs to.
   */
  PARENT_ENTITY_ID: 'parentEntityId',
  /**
   * Period a flux deep link points at, as the line key stores it: period end
   * plus cadence. Built in `buildFluxLineDeepLinkQuery` (which says why it
   * travels unresolved), resolved in `useFluxDeepLinkTimeframe`.
   */
  FLUX_PERIOD_END: 'flux_period_end',
  FLUX_PERIOD_UNIT: 'flux_period_unit',
} as const;

const TENANT_ROOT = `/[${ROUTE_PARAMS.TENANT}]` as const;

const DASHBOARD_ROUTE = `${TENANT_ROOT}/[${ROUTE_PARAMS.SUBSIDIARY}]` as const;

const CLOSE_MANAGEMENT_ROUTE = `${DASHBOARD_ROUTE}/close` as const;

const SUBSIDIARIES_ROUTE = `${DASHBOARD_ROUTE}/settings/subsidiaries`;

/** Object with all routes that are used in app */
export const ROUTES = {
  APP_ROOT: '/',
  LOGIN: '/login',
  REFRESH_SESSION: '/refresh-session',
  SESSION_EXPIRED: '/session-expired',
  LOGOUT: '/logout',
  ERROR: '/error',
  REDIRECT: '/redirect',
  /** Public landing page for the "Unsubscribe" link in notification emails */
  UNSUBSCRIBE: '/unsubscribe',
  /** One-click endpoint behind the `List-Unsubscribe-Post` email header */
  UNSUBSCRIBE_API: '/api/external/unsubscribe',
  TENANT_ROOT,
  FLUX_REDIRECT: `${TENANT_ROOT}/r/flux`,
  SUBSIDIARY_FLUX_REDIRECT: `${DASHBOARD_ROUTE}/r/flux`,
  CREATE_ENVIRONMENT: '/create-environment',
  CHART_OF_ACCOUNTS: `${DASHBOARD_ROUTE}/settings/coa`,
  DASHBOARD: `${DASHBOARD_ROUTE}/dashboard`,
  EXECUTIVE_SUMMARY: `${DASHBOARD_ROUTE}/executive-summary`,
  COA_EDIT: {
    DEFAULT: `${DASHBOARD_ROUTE}/settings/coa/edit`,
    RULES: `${DASHBOARD_ROUTE}/settings/coa/edit/rules`,
    UNMAPPED_ACCOUNTS: `${DASHBOARD_ROUTE}/settings/coa/edit/unmapped-accounts`,
    MAPPED_COA: `${DASHBOARD_ROUTE}/settings/coa/edit/mapped-coa`,
    CLEARING_GROUPS: `${DASHBOARD_ROUTE}/settings/coa/edit/clearing-groups`,
  },
  LEDGERS: `${DASHBOARD_ROUTE}/ledgers`,
  NOMINAL_LEDGER: `${DASHBOARD_ROUTE}/ledgers/nominal-ledger`,
  GENERAL_LEDGER: `${DASHBOARD_ROUTE}/ledgers/general-ledger`,
  NOMINAL_LEDGER_SELECT_ENTITY_TYPE: `${DASHBOARD_ROUTE}/ledgers/entry`,
  NOMINAL_LEDGER_ADD_ENTRY: `${DASHBOARD_ROUTE}/ledgers/entry/[${ROUTE_PARAMS.JE_ENTRY_TYPE}]`,
  NOMINAL_LEDGER_EDIT_ENTRY: `${DASHBOARD_ROUTE}/ledgers/entry/[${ROUTE_PARAMS.JE_ENTRY_TYPE}]/[${ROUTE_PARAMS.JE_ENTRY_ID}]`,
  DIMENSIONS: {
    VALUES: `${DASHBOARD_ROUTE}/settings/dimensions/values`,
    RULES: `${DASHBOARD_ROUTE}/settings/dimensions/rules`,
  },
  REPORT_CENTER: {
    DEFAULT: `${DASHBOARD_ROUTE}/report-center`,
    REPORT: `${DASHBOARD_ROUTE}/report-center/report`,
    DETAILS: `${DASHBOARD_ROUTE}/report-center/report/[${ROUTE_PARAMS.ENTITY_ID}]`,
    SUB_LEDGER: `${DASHBOARD_ROUTE}/report-center/subledger`,
    ACCOUNT: `${DASHBOARD_ROUTE}/report-center/account/[${ROUTE_PARAMS.ENTITY_ID}]`,
    ACCOUNT_REGISTER: `${DASHBOARD_ROUTE}/report-center/account/[${ROUTE_PARAMS.ENTITY_ID}]/register`,
    ACCOUNT_JOURNAL: `${DASHBOARD_ROUTE}/report-center/account/[${ROUTE_PARAMS.ENTITY_ID}]/account_journal`,
    FLUX: `${DASHBOARD_ROUTE}/report-center/flux`,
    FLUX_REPORT: `${DASHBOARD_ROUTE}/report-center/flux/[${ROUTE_PARAMS.ENTITY_ID}]`,
    FLUX_INVESTIGATION: `${DASHBOARD_ROUTE}/report-center/account/[${ROUTE_PARAMS.ENTITY_ID}]/flux-investigation`,
  },
  REPORT_CRUD: {
    SELECT_REPORT_TYPE: `${DASHBOARD_ROUTE}/select-new-report-type`,
    CREATE: `${DASHBOARD_ROUTE}/new-report/[type]/[name]`,
    EDIT: `${DASHBOARD_ROUTE}/edit-report/[${ROUTE_PARAMS.ENTITY_ID}]`,
  },
  ACTIVITY: {
    DEFAULT: CLOSE_MANAGEMENT_ROUTE,
    PERIOD: `${CLOSE_MANAGEMENT_ROUTE}/period/[period]`,
    DETAILS: `${CLOSE_MANAGEMENT_ROUTE}/period/[period]/activity/[${ROUTE_PARAMS.ENTITY_ID}]`,
    // Activity views are path segments (not a ?view= query param) handled by
    // the [[...view]] optional catch-all under the activity detail route.
    DETAILS_RECONCILIATIONS: `${CLOSE_MANAGEMENT_ROUTE}/period/[period]/activity/[${ROUTE_PARAMS.ENTITY_ID}]/reconciliations`,
    DETAILS_FLUX: `${CLOSE_MANAGEMENT_ROUTE}/period/[period]/activity/[${ROUTE_PARAMS.ENTITY_ID}]/flux`,
    DETAILS_APP: `${CLOSE_MANAGEMENT_ROUTE}/period/[period]/activity/[${ROUTE_PARAMS.ENTITY_ID}]/app/[slug]`,
    CREATE: `${CLOSE_MANAGEMENT_ROUTE}/period/[period]/activity/new`,
    EDIT: `${CLOSE_MANAGEMENT_ROUTE}/period/[period]/activity/[${ROUTE_PARAMS.ENTITY_ID}]/edit`,
  },
  TRIGGER: {
    CREATE: `${DASHBOARD_ROUTE}/new-agent`,
    EDIT: `${DASHBOARD_ROUTE}/edit-agent/[${ROUTE_PARAMS.ENTITY_ID}]`,
  },
  TASK: {
    DEFAULT: `${CLOSE_MANAGEMENT_ROUTE}/tasks`,
    CREATE: `${CLOSE_MANAGEMENT_ROUTE}/task/new`,
    EDIT: `${CLOSE_MANAGEMENT_ROUTE}/task/[${ROUTE_PARAMS.ENTITY_ID}]`,
    EDIT_OUTPUT: `${CLOSE_MANAGEMENT_ROUTE}/task/[${ROUTE_PARAMS.ENTITY_ID}]/output`,
    EDIT_INPUT: `${CLOSE_MANAGEMENT_ROUTE}/task/[${ROUTE_PARAMS.ENTITY_ID}]/input`,
    CREATE_JE: `${CLOSE_MANAGEMENT_ROUTE}/task/[${ROUTE_PARAMS.ENTITY_ID}]/create-je`,
    SETTINGS: `${CLOSE_MANAGEMENT_ROUTE}/task/[${ROUTE_PARAMS.ENTITY_ID}]/settings`,
    SETTINGS_INPUT: `${CLOSE_MANAGEMENT_ROUTE}/task/[${ROUTE_PARAMS.ENTITY_ID}]/settings/input`,
    SETTINGS_BY_DEFINITION: `${CLOSE_MANAGEMENT_ROUTE}/task/settings/[${ROUTE_PARAMS.ENTITY_ID}]`,
  },
  // Reconciliation tasks render a dedicated, recon-aware page/aside. They live
  // on their own URL prefix so the route (not a runtime fetch) decides which
  // page component loads — see the RECONCILIATION_TASK route tree and the
  // redirect guard on the plain TASK detail page.
  RECONCILIATION_TASK: {
    EDIT: `${CLOSE_MANAGEMENT_ROUTE}/reconciliation-task/[${ROUTE_PARAMS.ENTITY_ID}]`,
    EDIT_OUTPUT: `${CLOSE_MANAGEMENT_ROUTE}/reconciliation-task/[${ROUTE_PARAMS.ENTITY_ID}]/output`,
    EDIT_INPUT: `${CLOSE_MANAGEMENT_ROUTE}/reconciliation-task/[${ROUTE_PARAMS.ENTITY_ID}]/input`,
    CREATE_JE: `${CLOSE_MANAGEMENT_ROUTE}/reconciliation-task/[${ROUTE_PARAMS.ENTITY_ID}]/create-je`,
    SETTINGS: `${CLOSE_MANAGEMENT_ROUTE}/reconciliation-task/[${ROUTE_PARAMS.ENTITY_ID}]/settings`,
    SETTINGS_INPUT: `${CLOSE_MANAGEMENT_ROUTE}/reconciliation-task/[${ROUTE_PARAMS.ENTITY_ID}]/settings/input`,
  },
  RECONCILIATIONS: {
    DEFAULT: `${CLOSE_MANAGEMENT_ROUTE}/reconciliations`,
  },
  ACCOUNT_RECS: {
    DEFAULT: `${CLOSE_MANAGEMENT_ROUTE}/account-recs`,
  },
  AGENT: {
    DEFAULT: `${CLOSE_MANAGEMENT_ROUTE}/agents`,
    EXECUTIONS: `${CLOSE_MANAGEMENT_ROUTE}/agents/executions`,
    CREATE: `${CLOSE_MANAGEMENT_ROUTE}/agent/new`,
    EDIT: `${CLOSE_MANAGEMENT_ROUTE}/agent/[${ROUTE_PARAMS.ENTITY_ID}]/edit`,
    DETAILS: `${CLOSE_MANAGEMENT_ROUTE}/agent/[${ROUTE_PARAMS.ENTITY_ID}]`,
    EXECUTION: `${CLOSE_MANAGEMENT_ROUTE}/agent/[${ROUTE_PARAMS.ENTITY_ID}]/execution/[${ROUTE_PARAMS.DETAILS_ID}]`,
    SETTINGS: `${CLOSE_MANAGEMENT_ROUTE}/agent/[${ROUTE_PARAMS.ENTITY_ID}]/settings`,
  },
  APPS: {
    DEFAULT: `${DASHBOARD_ROUTE}/apps`,
    APP: `${DASHBOARD_ROUTE}/apps/[slug]`,
    BRIDGE: `${DASHBOARD_ROUTE}/apps/bridge`,
    BUILDER: `${DASHBOARD_ROUTE}/apps/builder`,
  },
  AGENT_STUDIO: {
    DEFAULT: `${DASHBOARD_ROUTE}/agent-studio`,
  },
  ASSISTANT: {
    DEFAULT: `${DASHBOARD_ROUTE}/assistant`,
    THREAD: `${DASHBOARD_ROUTE}/assistant/[${ROUTE_PARAMS.ENTITY_ID}]`,
  },
  WORKFLOWS: {
    DEFAULT: `${DASHBOARD_ROUTE}/workflows`,
  },
  TRANSACTION_PATROL: {
    DEFAULT: `${DASHBOARD_ROUTE}/transaction-patrol`,
  },
  NOMINAL_HUB: {
    DEFAULT: `${DASHBOARD_ROUTE}/hub`,
    BILLS: {
      DEFAULT: `${DASHBOARD_ROUTE}/hub/bills`,
      DETAILS: `${DASHBOARD_ROUTE}/hub/bills/[${ROUTE_PARAMS.ENTITY_ID}]`,
    },
  },
  MANAGE_SUBSIDIARIES: {
    DEFAULT: SUBSIDIARIES_ROUTE,
    DETAILS: `${SUBSIDIARIES_ROUTE}/[${ROUTE_PARAMS.ENTITY_ID}]`,
    RULES: `${SUBSIDIARIES_ROUTE}/[${ROUTE_PARAMS.ENTITY_ID}]/rules`,
    UNMAPPED_ACCOUNTS: `${SUBSIDIARIES_ROUTE}/[${ROUTE_PARAMS.ENTITY_ID}]/unmapped-accounts`,
    MAPPED_COA: `${SUBSIDIARIES_ROUTE}/[${ROUTE_PARAMS.ENTITY_ID}]/mapped-coa`,
    CLEARING_GROUPS: {
      DEFAULT: `${SUBSIDIARIES_ROUTE}/[${ROUTE_PARAMS.ENTITY_ID}]/clearing-groups`,
    },
    CREATE: {
      SUBSIDIARY: `${SUBSIDIARIES_ROUTE}?entity=subsidiary`,
      GROUP: `${SUBSIDIARIES_ROUTE}?entity=group`,
      ORGANIZATION: `${SUBSIDIARIES_ROUTE}?entity=organization`,
    },
    BVA: `${SUBSIDIARIES_ROUTE}/[${ROUTE_PARAMS.ENTITY_ID}]/bva`,
  },
  DOWNLOAD_ATTACHMENT: `${DASHBOARD_ROUTE}/attachment/[${ROUTE_PARAMS.ENTITY_ID}]`,
  DOWNLOAD_RECONCILIATION_SOURCE_VERSION: `${DASHBOARD_ROUTE}/reconciliation-sources/[${ROUTE_PARAMS.SOURCE_ID}]/versions/[${ROUTE_PARAMS.VERSION_ID}]/download`,
  CONSOLIDATION_EXCHANGE_RATES: `${DASHBOARD_ROUTE}/settings/exchange-rates`,
  PLAYGROUND: {
    DEFAULT: `${DASHBOARD_ROUTE}/playground`,
    FLUX_EMAIL: `${DASHBOARD_ROUTE}/playground/flux-email`,
  },
  AUTO_ICT: {
    DEFAULT: `${DASHBOARD_ROUTE}/auto-ict/recommended`,
    RECOMMENDED: `${DASHBOARD_ROUTE}/auto-ict/recommended`,
    RECOMMENDED_MUI: `${DASHBOARD_ROUTE}/auto-ict/recommended-mui`,
    UNMATCHED: `${DASHBOARD_ROUTE}/auto-ict/unmatched`,
    UNMATCHED_MUI: `${DASHBOARD_ROUTE}/auto-ict/unmatched-mui`,
    PENDING: `${DASHBOARD_ROUTE}/auto-ict/pending`,
    MATCHED: `${DASHBOARD_ROUTE}/auto-ict/matched`,
    MATCHED_MUI: `${DASHBOARD_ROUTE}/auto-ict/matched-mui`,
    SIGNED_OFF: `${DASHBOARD_ROUTE}/auto-ict/signedoff`,
    SIGNED_OFF_MUI: `${DASHBOARD_ROUTE}/auto-ict/signedoff-mui`,
    REVIEW_TASK: `${DASHBOARD_ROUTE}/auto-ict/review-task`,
    RESOLVE_TASK: `${DASHBOARD_ROUTE}/auto-ict/resolve-task`,
    MATCHING_SETS: `${DASHBOARD_ROUTE}/auto-ict/matching-sets`,
    ADJUSTMENTS: `${DASHBOARD_ROUTE}/auto-ict/adjustments/[${ROUTE_PARAMS.ENTITY_ID}]`,
  },
  MATCHING_GROUP: {
    CREATE: `${DASHBOARD_ROUTE}/matching-group/new`,
    EDIT: `${DASHBOARD_ROUTE}/matching-group/[${ROUTE_PARAMS.ENTITY_ID}]`,
  },
  MATCHING_AGENTS: {
    DEFAULT: `${DASHBOARD_ROUTE}/matching-agents`,
  },
  MATCHING_AGENT: {
    DEFAULT: `${DASHBOARD_ROUTE}/auto-ict/agent/[${ROUTE_PARAMS.ENTITY_ID}]`,
  },
  INTEGRATIONS_SETTINGS: {
    DEFAULT: `${DASHBOARD_ROUTE}/settings/integrations`,
    CREATE: {
      NOTIFICATIONS: `${DASHBOARD_ROUTE}/settings/integrations?entity=notifications`,
      BANK: `${DASHBOARD_ROUTE}/settings/integrations?entity=bank`,
    },
  },
  BANK_RECONCILIATION: {
    DEFAULT: `${DASHBOARD_ROUTE}/bank-reconciliation`,
    RECOMMENDED: `${DASHBOARD_ROUTE}/bank-reconciliation/recommended/[${ROUTE_PARAMS.ENTITY_ID}]`,
    UNMATCHED: `${DASHBOARD_ROUTE}/bank-reconciliation/unmatched/[${ROUTE_PARAMS.ENTITY_ID}]`,
    MATCHED: `${DASHBOARD_ROUTE}/bank-reconciliation/matched/[${ROUTE_PARAMS.ENTITY_ID}]`,
    STATEMENTS: `${DASHBOARD_ROUTE}/bank-reconciliation/statements/[${ROUTE_PARAMS.ENTITY_ID}]`,
    ADJUSTMENTS: `${DASHBOARD_ROUTE}/bank-reconciliation/adjustments/[${ROUTE_PARAMS.ENTITY_ID}]`,
  },
  BANK_RECONCILIATION_PROTOTYPE: {
    DEFAULT: `${DASHBOARD_ROUTE}/bank-reconciliation-preview`,
  },
  SYNC_HEALTH: `${DASHBOARD_ROUTE}/settings/sync-health`,
  PYLON_AUTH: '/api/auth/pylon',
} as const;

/** Array of public routes that are accessible without login */
export const PUBLIC_ROUTES = [
  ROUTES.LOGIN,
  ROUTES.LOGOUT,
  ROUTES.REDIRECT,
  ROUTES.REFRESH_SESSION,
  ROUTES.SESSION_EXPIRED,
  ROUTES.UNSUBSCRIBE,
  ROUTES.UNSUBSCRIBE_API,
  '/api/external/webhooks/liveblocks',
  '/api/external/webhooks/descope/new-user',
  '/api/internal/event',
  '/api/internal/system-status',
  '/api/csp-report',
  ROUTES.PYLON_AUTH,
] as const;

/**
 * API route prefixes the proxy lets through without a session. PUBLIC_ROUTES
 * matches exact paths only, so routes with a dynamic segment go here. Every
 * route under a listed prefix must do its own m2m authentication. The
 * `satisfies` clause pins the `/api/` shape: the proxy only consults this
 * list for API paths, so a page prefix here would silently do nothing.
 */
export const PUBLIC_API_ROUTE_PREFIXES = [
  '/api/internal/flows/',
] as const satisfies readonly `/api/${string}/`[];

export const ROUTES_WITHOUT_TENANT = [
  ROUTES.CREATE_ENVIRONMENT,
  ROUTES.LOGOUT,
  ROUTES.SESSION_EXPIRED,
  ROUTES.ERROR,
  ROUTES.REDIRECT,
  ROUTES.UNSUBSCRIBE,
  /* Automation only route */
  '/internal-automation',
  /* Sometimes we get route but not populated with tenant */
  '/[tenant]',
  '/api',
  '/dev-utils',
] as const;

export const EXPORTER_ROUTES = {
  FLUX_DASHBOARD: `/exporter${DASHBOARD_ROUTE}/flux-dashboard`,
  ACCOUNT_REPORT: `/exporter${DASHBOARD_ROUTE}/account-report/[${ROUTE_PARAMS.ENTITY_ID}]`,
};

export type RouteParamsObject = {
  [key in RouteParamKeys]?: any;
};

export const populateRoute = (
  route: string,
  params: RouteParamsObject = {},
) => {
  if (params && typeof params === 'object') {
    for (const [key, value] of Object.entries(params)) {
      route = route?.replace(`[${key}]`, String(value));
    }
  }

  return route;
};

export const hasRouteBeforeTenant = (url: string) =>
  ['exporter'].some((route) => url.includes(route));

// For some reason (still unknown) these routes are sometimes marked as tenant routes
export const VERCEL_ROUTES = ['_next', '_vercel', '.ico', 'well-known'];

/** Check if a URL segment matches a known system route (substring match) */
export const isSystemRoute = (segment: string) =>
  VERCEL_ROUTES.some((route) => segment.includes(route));
